import React from 'react';

import { Alert, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';

import { ParentFlightOrder, getParentFlightOrderLabel } from './parentFlightOrderUtils';

const NO_ELIGIBLE_PARENT_ORDERS_MESSAGE =
  'This customer has no completed flight orders with upcoming travel, so a post-purchase flight offer cannot be created from this account. Make sure you are on the account that placed the original order.';

interface Props {
  parentOrders: Array<ParentFlightOrder>;
  isLoading: boolean;
  isError: boolean;
  value: string;
  onChange: (orderId: string) => void;
  disabled?: boolean;
  errorMessage?: string;
}

export default function ParentFlightOrderSelect({
  parentOrders,
  isLoading,
  isError,
  value,
  onChange,
  disabled = false,
  errorMessage,
}: Props) {
  if (isError) {
    return (
      <Alert severity="error" data-testid="parent-order-error">
        Could not load this customer's orders. Reload the page to try again.
      </Alert>
    );
  }

  if (!isLoading && parentOrders.length === 0 && !value) {
    return (
      <Alert severity="warning" data-testid="parent-order-empty">
        {NO_ELIGIBLE_PARENT_ORDERS_MESSAGE}
      </Alert>
    );
  }

  // An offer saved earlier may point at an order that has since flown; keep it selectable so the
  // edit form still shows what it is linked to.
  const hasOptionForValue = parentOrders.some((parent) => parent.order.id_orders === value);

  return (
    <FormControl fullWidth error={!!errorMessage} disabled={disabled || isLoading}>
      <InputLabel id="parent-order-label" required>
        Parent order
      </InputLabel>
      <Select
        labelId="parent-order-label"
        label="Parent order"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="parent-order"
      >
        {value && !hasOptionForValue && <MenuItem value={value}>Order {value}</MenuItem>}
        {parentOrders.map((parent) => (
          <MenuItem key={parent.order.id_orders} value={parent.order.id_orders}>
            {getParentFlightOrderLabel(parent)}
          </MenuItem>
        ))}
      </Select>
      {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
}
